import chickenSound from "@assets/sounds/chicken.mp3";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import CardSpacer from "@dashboard/components/CardSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import Money from "@dashboard/components/Money";
import RequirePermissions from "@dashboard/components/RequirePermissions";
import { PaymentChargeStatusEnum, PermissionEnum } from "@dashboard/graphql";
import {
  Activities,
  Analitics,
  HomeData,
  Notifications,
  OutOfStockDetail,
  ProductTopToday,
} from "@dashboard/home/types";
import { orderListUrl } from "@dashboard/orders/urls";
import { Box, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { HomeActivityCard } from "../HomeActivityCard";
import { HomeAnalyticsCard } from "../HomeAnalyticsCard";
import { HomeHeader } from "../HomeHeader";
import { HomeOutOfStockPanel } from "../HomeOutOfStockPanel";
import { HomeProductList } from "../HomeProductList";
import { HomeSalesPanel } from "../HomeSalesPanel";
import { homeSalesPanelMessages } from "../HomeSalesPanel/messages";
import { homePageMessages } from "./messages";

export interface HomePageProps {
  activities: HomeData<Activities>;
  analitics: HomeData<Analitics>;
  topProducts: HomeData<ProductTopToday>;
  notifications: HomeData<Notifications>;
  outOfStockDetail: HomeData<OutOfStockDetail>;
  onOutOfStockExpand: () => void;
  today: string;
  userName: string;
  noChannel: boolean;
}

const HomePage: React.FC<HomePageProps> = props => {
  const {
    userName,
    analitics,
    topProducts,
    activities,
    notifications,
    outOfStockDetail,
    onOutOfStockExpand,
    today,
    noChannel,
  } = props;
  const intl = useIntl();

  const chickenAudio = React.useRef<HTMLAudioElement>();

  const playChicken = () => {
    if (!chickenAudio.current) {
      chickenAudio.current = new Audio(chickenSound);
    }

    // Restart rather than ignore the hover while the last cluck is still going.
    chickenAudio.current.currentTime = 0;
    // Autoplay is blocked until the document has been interacted with, and a
    // hover is not an interaction — so a rejection here is expected, not a bug.
    chickenAudio.current.play().catch(() => undefined);
  };

  const stopChicken = () => {
    if (!chickenAudio.current) {
      return;
    }

    chickenAudio.current.pause();
    chickenAudio.current.currentTime = 0;
  };

  const ordersListUrl = orderListUrl({
    createdFrom: today,
    createdTo: today,
  });

  const renderSalesFigure = () => {
    if (noChannel || analitics.hasError) {
      // Not zero: zero would claim a day with no trade.
      return intl.formatMessage(homeSalesPanelMessages.unavailable);
    }

    if (analitics.loading) {
      return <Skeleton style={{ width: "5em" }} />;
    }

    return analitics.data.sales ? (
      <Money money={analitics.data.sales} />
    ) : (
      intl.formatMessage(homeSalesPanelMessages.unavailable)
    );
  };

  // Orders the shop has not been paid for. Filtered, not just "today's
  // orders" — otherwise this lands on the same list as the footer link.
  const unpaidOrdersUrl = orderListUrl({
    createdFrom: today,
    createdTo: today,
    paymentStatus: [PaymentChargeStatusEnum.NOT_CHARGED],
  });

  // Nothing interactive belongs here: this renders inside Accordion.Trigger,
  // which is a <button>, so a nested link would both navigate and toggle.
  const salesSecondaryLine =
    !noChannel &&
    !analitics.hasError &&
    !analitics.loading &&
    analitics.data.isPartialDay ? (
      <Text size="small" color="critical1">
        {intl.formatMessage(homeSalesPanelMessages.partialDay)}
      </Text>
    ) : undefined;

  return (
    <DetailPageLayout withSavebar={false}>
      <TopNav title={<HomeHeader userName={userName} />} />
      <DetailPageLayout.Content>
        <Box paddingLeft={6} paddingRight={8}>
          <CardSpacer />
          <RequirePermissions
            requiredPermissions={[PermissionEnum.MANAGE_ORDERS]}
          >
            <Box
              display="grid"
              __gridTemplateColumns="repeat(2, 1fr)"
              gap={5}
              marginBottom={5}
            >
              <HomeAnalyticsCard
                title={intl.formatMessage(homePageMessages.salesCardTitle)}
                testId="sales-analytics"
                secondaryLine={salesSecondaryLine}
                detail={
                  <HomeSalesPanel
                    orders={analitics.data.orders ?? []}
                    loading={analitics.loading}
                    ordersListUrl={ordersListUrl}
                    awaitingPaymentCount={analitics.data.awaitingPaymentCount}
                    unpaidOrdersUrl={unpaidOrdersUrl}
                  />
                }
              >
                {renderSalesFigure()}
              </HomeAnalyticsCard>
              <HomeAnalyticsCard
                title={intl.formatMessage(homePageMessages.outOfStockCardTitle)}
                testId="out-of-stock-analytics"
                onFirstExpand={onOutOfStockExpand}
                detail={
                  <HomeOutOfStockPanel
                    products={outOfStockDetail.data.products}
                    totalCount={outOfStockDetail.data.totalCount}
                    loading={outOfStockDetail.loading}
                  />
                }
              >
                {noChannel || notifications.hasError ? (
                  0
                ) : !notifications.loading ? (
                  notifications.data.productsOutOfStock
                ) : (
                  <Skeleton style={{ width: "5em" }} />
                )}
              </HomeAnalyticsCard>
            </Box>
          </RequirePermissions>
          <CardSpacer />
          {topProducts && (
            <RequirePermissions
              requiredPermissions={[
                PermissionEnum.MANAGE_ORDERS,
                PermissionEnum.MANAGE_PRODUCTS,
              ]}
            >
              <HomeProductList
                testId="top-products"
                topProducts={topProducts}
              />
              <CardSpacer />
            </RequirePermissions>
          )}
        </Box>
      </DetailPageLayout.Content>
      {activities && (
        <DetailPageLayout.RightSidebar>
          <RequirePermissions
            requiredPermissions={[PermissionEnum.MANAGE_ORDERS]}
          >
            <HomeActivityCard activities={activities} testId="activity-card" />
          </RequirePermissions>
        </DetailPageLayout.RightSidebar>
      )}
      <Box
        as="span"
        position="fixed"
        __bottom="16px"
        __right="16px"
        __fontSize="24px"
        cursor="pointer"
        aria-hidden="true"
        data-test-id="home-chicken"
        onMouseEnter={playChicken}
        onMouseLeave={stopChicken}
      >
        🐔
      </Box>
    </DetailPageLayout>
  );
};
HomePage.displayName = "HomePage";
export default HomePage;
